import React, {useEffect, useState} from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
// Material-UI imports
import {Box, Button, Container, Divider, Grid, Paper} from "@material-ui/core";
// Project imports
import Utils, {API, createApiConfig} from "../../../utils";
import PersonEmails from "../Emails/PersonEmails";
import {LoadingBackdrop} from "../../common/Loading";
import {makeFullName, ProfileDTO, ProfileUpdateDTO, PublicProfileDTO, toPersonUpdateDTO} from "../../../models/user";
import {CommunicationError} from "../../common/errors";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import EditIcon from "@material-ui/icons/Edit";
import {UploadableImage} from "../../common/images";
import EmptyAvatar from "../../../graphics/avatar.png";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {AxiosError, AxiosResponse} from "axios";
import {ImageDTO} from "../../../models/images";
import {API_URL} from "../../../utils/API";
import {ProfileForm} from "./ProfileForm";
import {RouteComponentProps} from "react-router-dom";
import {MarkdownRender} from "../../common/markdown";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        pageActions: {
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginBottom: theme.spacing(2),
        },
        actionButton: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
        },
        profileCard: {
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
        },
        avatarWrapper: {
            width: 120,
            height: 120,
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#F3F4F6",
            border: "1px solid #E5E7EB",
        },
        avatarImage: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        profileName: {
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "#111827",
            margin: 0,
            marginBottom: theme.spacing(1),
        },
        divider: {
            backgroundColor: "#E5E7EB",
            marginBottom: theme.spacing(1.5),
            marginTop: theme.spacing(0.5),
        },
        aboutLabel: {
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#111827",
            marginBottom: theme.spacing(0.5),
        },
        aboutText: {
            color: "#374151",
            fontSize: "0.9375rem",
            lineHeight: 1.6,
        },
        emailSection: {
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            padding: theme.spacing(3),
        },
        emailSectionTitle: {
            fontSize: "1rem",
            fontWeight: 700,
            color: "#111827",
            marginBottom: theme.spacing(2),
        },
    })
);

type ProfileRouteParams = {
    personId: string;
};

export interface ProfileRouteProps
    extends RouteComponentProps<ProfileRouteParams>,
        React.ClassAttributes<ProfileRouteParams> {}

export interface ProfileProps {
    personId?: string;
}

interface ProfileContentState {
    profile: ProfileDTO | PublicProfileDTO | null;
    name: "loading" | "loaded" | "edit" | "editing" | "saving" | "failed";
    notification: "edit_ok" | "edit_fail" | "avatar_ok" | "avatar_fail" | "no_changes" | null;
    avatar: ImageDTO | null;
    personEdit: ProfileUpdateDTO | null;
}

const ProfileContent: React.FC<ProfileProps> = (props: ProfileProps) => {
    const [t] = useTranslation("user");
    const classes = useStyles();
    const {keycloak, initialized} = useKeycloak();
    const initState: ProfileContentState = {
        profile: null,
        notification: null,
        name: "loading",
        avatar: null,
        personEdit: null,
    };
    const [state, setState] = useState(initState);

    const isOwnProfile = !props.personId;

    useEffect(() => {
        if (state.name === "loading") {
            API.get<ProfileDTO | PublicProfileDTO>(
                isOwnProfile ? "/profile" : "/public-profile/" + props.personId,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    const avatar = res.data.person.profileImage;
                    setState({
                        ...state,
                        profile: res.data,
                        personEdit: toPersonUpdateDTO(res.data.person),
                        avatar: avatar,
                        name: "loaded",
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        } else if (state.name === "saving") {
            API.put<ProfileDTO>(`/profile`, state.personEdit, createApiConfig(keycloak, initialized))
                .then((res) => {
                    const avatar = res.data.person.profileImage;
                    setState({
                        ...state,
                        profile: res.data,
                        personEdit: toPersonUpdateDTO(res.data.person),
                        avatar: avatar,
                        name: "loaded",
                        notification: "edit_ok",
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "loaded", notification: "edit_fail"});
                });
        }
    }, [keycloak, initialized, state, setState]);

    const submitChanges = (person: ProfileUpdateDTO): void => {
        if (state.profile === null || state.personEdit === null) return;
        const changed = Utils.objEq(toPersonUpdateDTO(state.profile.person), state.personEdit, [
            "firstname",
            "lastname",
            "titlesBefore",
            "titlesAfter",
            "bio",
        ]);
        if (changed) {
            setState({...state, name: "saving", personEdit: person});
        } else {
            setState({...state, name: "loaded", notification: "no_changes"});
        }
    };
    const startEdit = (): void => {
        setState({...state, name: "editing"});
    };
    const stopEdit = (): void => {
        setState({...state, name: "loaded"});
    };
    const handleAvatarUpload = (file: File) => {
        const formData = new FormData();
        formData.append("image", file);
        API.post<ImageDTO>(`/profile/avatar`, formData, createApiConfig(keycloak, initialized))
            .then((res: AxiosResponse<ImageDTO>) => {
                setState({...state, name: "loaded", avatar: res.data, notification: "avatar_ok"});
            })
            .catch((err: AxiosError) => {
                setState({...state, name: "failed", notification: "avatar_fail"});
            });
    };

    const changePassLink = keycloak.createAccountUrl().replace("/account", "/account/password");

    if (state.name === "failed") {
        return <CommunicationError />;
    } else if (state.name === "loading") {
        return <LoadingBackdrop />;
    } else if (state.profile !== null && state.personEdit !== null) {
        const profile = state.profile;
        const avatarUrl = `${API_URL}/profile/avatar?timestamp=${state.avatar?.updatedAt}`;

        const profileForm = <ProfileForm profile={state.personEdit} onSubmit={submitChanges} onCancel={stopEdit} />;

        const personInfo = (
            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs="auto">
                    <UploadableImage
                        key={state.avatar?.updatedAt ?? "emptyAvatar"}
                        src={avatarUrl}
                        wrapperClass={classes.avatarWrapper}
                        imageClass={classes.avatarImage}
                        alt={makeFullName(state.profile.person)}
                        onConfirm={handleAvatarUpload}
                        emptyImage={EmptyAvatar}
                        editable={isOwnProfile}
                    />
                </Grid>
                <Grid item xs>
                    <h2 className={classes.profileName}>{makeFullName(state.profile.person)}</h2>
                    <Divider className={classes.divider} />
                    {state.profile.person.bio ? (
                        <>
                            <div className={classes.aboutLabel}>About you</div>
                            <div className={classes.aboutText}>
                                <MarkdownRender value={state.profile.person.bio ?? ""} />
                            </div>
                        </>
                    ) : null}
                </Grid>
            </Grid>
        );

        return (
            <Container maxWidth="md">
                {/* Top action buttons */}
                {isOwnProfile && (
                    <Box className={classes.pageActions}>
                        <Button
                            variant="contained"
                            color="primary"
                            href={changePassLink}
                            startIcon={<VpnKeyIcon />}
                            className={classes.actionButton}
                        >
                            {t("profile.changePassword")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={startEdit}
                            disabled={state.name === "editing"}
                            startIcon={<EditIcon />}
                            className={classes.actionButton}
                        >
                            {t("profile.editProfile")}
                        </Button>
                    </Box>
                )}

                {/* Profile card */}
                <Paper className={classes.profileCard} elevation={0}>
                    {state.name === "editing" ? profileForm : personInfo}
                </Paper>

                {/* Email section */}
                {state.name === "loaded" && (
                    <Paper className={classes.emailSection} elevation={0}>
                        <div className={classes.emailSectionTitle}>{t("emails.email")}</div>
                        <PersonEmails personUuid={profile.person.id} emails={profile.emails} own={isOwnProfile} />
                    </Paper>
                )}

                {state.name === "saving" && <LoadingBackdrop />}
            </Container>
        );
    } else {
        return <CommunicationError />;
    }
};

const Profile: React.FC<ProfileRouteProps> = (props: ProfileRouteProps) => {
    return <ProfileContent personId={props.match.params.personId} />;
};

export const OwnProfile: React.FC = () => {
    return <ProfileContent />;
};

export default Profile;
